import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

function Pill({ status }) {
  const cls = status === "paid" ? "status-paid" : "status-unpaid";
  return <span className={`status-pill ${cls}`} data-testid={`pill-${status}`}>{status}</span>;
}

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/orders")]).then(([inv, ord]) => {
      setInvoices(inv.data);
      setOrders(ord.data);
      setLoading(false);
    });
  }, []);

  // Outstanding for the current calendar month — naturally resets to zero on the 1st
  const now = new Date();
  const monthOutstanding = invoices
    .filter((i) => {
      const d = new Date(i.created_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((s, i) => s + i.total, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">Dashboard</p>
            <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1" data-testid="dashboard-title">Your account</h1>
          </div>
          <Link to="/catalog"><Button className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="dashboard-new-order">New order</Button></Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <Card label="Outstanding (this month)" value={inr(monthOutstanding)} testid="stat-outstanding" emphasis />
          <Card label="Orders" value={orders.length} testid="stat-orders" />
        </div>

        <h2 className="font-heading font-semibold text-xl mb-4">Recent invoices</h2>
        <div className="border border-gray-200">
          <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
            <div className="col-span-4">Invoice</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-1"/>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-gray-500 mb-3" data-testid="no-invoices">No invoices yet.</p>
              <Link to="/catalog"><Button variant="outline" className="rounded-sm">Place your first order</Button></Link>
            </div>
          ) : (
            invoices.slice(0, 10).map((inv) => (
              <Link key={inv.id} to={`/invoices/${inv.id}`} className="grid grid-cols-12 px-4 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors items-center" data-testid={`inv-row-${inv.id}`}>
                <div className="col-span-4 font-medium text-sm">{inv.zoho?.zoho_invoice_number || `#${inv.id.slice(-8)}`}</div>
                <div className="col-span-3 text-sm text-gray-500">{new Date(inv.created_at).toLocaleDateString()}</div>
                <div className="col-span-2"><Pill status={inv.status} /></div>
                <div className="col-span-2 text-right text-sm font-medium">{inr(inv.total)}</div>
                <div className="col-span-1 text-right text-gray-400"><ArrowRight className="w-4 h-4 inline" strokeWidth={1.5}/></div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, testid, emphasis }) {
  return (
    <div className={`border p-6 ${emphasis ? "border-[#002fa7] bg-blue-50" : "border-gray-200 bg-white"}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-medium">{label}</div>
      <div className="text-3xl font-heading font-semibold mt-2 tracking-tight">{value}</div>
    </div>
  );
}
